/**
 * Emoji-Reaktionen der Entwickler auf Nachrichten von Martin — gemeinsame Regeln. (18.10.2026, Martin 12:17)
 *
 * Statt einer Textantwort („Okay, perfekt“) setzt ein Entwickler ein Emoji an Martins Nachricht (wie Telegram/WhatsApp):
 * `node scripts/dreier-sagen.mjs --reagiere 👍`. Das Emoji ist KEIN Chat-Beitrag: nicht in chat_messages, nie im Prompt von
 * Vespera, wird nicht vorgelesen, öffnet kein Mikro. Es geht als eigenes Strom-Ereignis `reaktionen` an die offenen Seiten von
 * Konto 1 (server/reaktionen.ts) und steht in einer kleinen Ablage (data/reaktionen.json), damit es Neuladen und Neustart übersteht.
 * ⚠️ Ein Emoji eines Entwicklers ist nie Martins Ja und nie eine Freigabe.
 */
import { istEntwicklerId, type EntwicklerId } from "./entwickler.js";

/** Name des SSE-Ereignisses auf dem Chat-Strom. */
export const REAKTIONEN_EREIGNIS = "reaktionen";

/** Die erlaubten Emojis (Whitelist) mit ihrem gesprochenen Namen (aria-label). */
export const REAKTIONEN: Readonly<Record<string, string>> = {
  "👍": "Daumen hoch",
  "👀": "Augen",
  "✅": "Haken",
  "🙏": "Danke",
  "🔧": "Schraubenschlüssel",
  "🤔": "nachdenklichem Gesicht",
};

/** Wie viele Reaktionen höchstens gespeichert werden / auf dem Strom stehen. */
export const REAKTIONEN_HOECHSTENS = 100;
/** So lange bleibt eine Reaktion in der Ablage. */
export const REAKTION_VERFALL_MS = 14 * 24 * 60 * 60_000;

export interface Reaktion {
  /** Welche Nachricht Martins: ihr Speicher-Zeitpunkt (ISO). */
  zeit: string;
  von: EntwicklerId;
  emoji: string;
  /** Wann gesetzt (ms seit 1970). */
  um: number;
}

export interface ReaktionenStand {
  reaktionen: Reaktion[];
}

/** Ein gültiges Emoji der Whitelist (ein Variantenselektor wird toleriert) — sonst null. Rein. */
export function reaktionEmojiAus(roh: unknown): string | null {
  if (typeof roh !== "string" || roh.length > 16) return null;
  const s = roh.trim().replace(/️/g, "");
  return Object.prototype.hasOwnProperty.call(REAKTIONEN, s) ? s : null;
}

/** Der gesprochene Satz: „Schmied reagiert mit Daumen hoch“. Rein. */
export function reaktionBeschriftung(name: string, emoji: string): string {
  return `${name} reagiert mit ${REAKTIONEN[emoji] ?? "Emoji"}`;
}

function istZeit(w: unknown): w is string {
  return typeof w === "string" && w.length <= 40 && Number.isFinite(Date.parse(w));
}

/** Prüft einen Stand vom Draht streng; Unbrauchbares fällt heraus. Rein. */
export function leseReaktionen(roh: unknown): ReaktionenStand | null {
  const liste = roh && typeof roh === "object" ? (roh as { reaktionen?: unknown }).reaktionen : null;
  if (!Array.isArray(liste)) return null;
  const aus: Reaktion[] = [];
  for (const e of liste.slice(0, REAKTIONEN_HOECHSTENS)) {
    if (!e || typeof e !== "object") continue;
    const { zeit, von, emoji, um } = e as Record<string, unknown>;
    if (!istZeit(zeit) || !istEntwicklerId(von)) continue;
    const em = reaktionEmojiAus(emoji);
    if (!em) continue;
    aus.push({ zeit, von, emoji: em, um: typeof um === "number" && Number.isFinite(um) ? um : 0 });
  }
  return { reaktionen: aus };
}
